"use client";

import { useDoActionWhenVisible } from "@faire/react/hooks/useDoActionWhenVisible";
import { SVGIconProps } from "@faire/slate-icons/types";
import { Button } from "@faire/slate/components/button";
import { mergeRefs } from "@faire/slate/lib/mergeRefs";
import { componentCls } from "@faire/slate/lib/tracking";
import { tsv } from "@faire/slate/lib/tsv";
import { useFocusTargetOutside } from "@faire/slate/private/hooks/useFocusTargetOutside";
import { useViewport } from "@faire/slate/private/hooks/useViewport";
import { getMessagingBannerIcon } from "@faire/slate/private/messaging-banner/getMessagingBannerIcon";
import { getMessagingBannerRole } from "@faire/slate/private/messaging-banner/getMessagingBannerRole";
import { MessagingBannerContent } from "@faire/slate/private/messaging-banner/MessagingBannerContent";
import { MessagingBannerDismissButton } from "@faire/slate/private/messaging-banner/MessagingBannerDismissButton";
import { MessagingBannerTapTarget } from "@faire/slate/private/messaging-banner/MessagingBannerTapTarget";
import {
  IMessagingBannerActionProps,
  IMessagingBannerPropsConstraints,
  IMessagingBannerWebProps,
  MessagingBannerVariant,
} from "@faire/slate/private/messaging-banner/types";
import { IText } from "@faire/slate/private/shared/types";
import React, { useCallback, useRef } from "react";

type IProps = {
  /**
   * Props for the inline action. `label` becomes the button text; the
   * remaining props are forwarded to the underlying `<Button>`, which always
   * renders as `variant="plain"`.
   *
   * On mobile the action renders as a chevron with a tap target covering the
   * whole banner, and the dismiss button is hidden, unless
   * `mobileVariant: "button"`.
   */
  actionProps?: IMessagingBannerActionProps;
  /**
   * Background color token.
   *
   * @invariant only allowed when `variant="custom"`
   */
  backgroundColor?: string;
  /**
   * Desktop-only: inline label, description, and action on a single row.
   * No-op on mobile.
   * @default false
   */
  condensed?: boolean;
  /**
   * Secondary text. Stacks beneath the label on mobile.
   */
  description?: IText;
  /**
   * Icon rendered at the start of the banner.
   *
   * @invariant only allowed when `variant="neutral"`, `variant="info"`, or `variant="custom"`
   */
  Icon?: React.FC<SVGIconProps>;
  /**
   * Label text color token.
   *
   * @invariant only allowed when `variant="custom"`
   */
  labelColor?: string;
  /**
   * Banner heading. Must stand on its own on mobile.
   */
  label?: string;
  /**
   * Banner color and icon family.
   * @default "neutral"
   */
  variant?: MessagingBannerVariant;
  /**
   * Renders a dismiss button. Consumer unmounts the banner.
   *
   * Ignored on mobile when `actionProps` is set, unless its `mobileVariant`
   * is `"button"`.
   */
  onDismiss?: () => void;
  /**
   * Fired once when the banner first scrolls into view. Useful for impression
   * tracking.
   */
  onView?: () => void;
  // DO NOT ADD any new prop without aligning with Slate team first
} & IMessagingBannerWebProps;

/**
 * {@link https://www.notion.so/faire/Messaging-Banner-36d2efb5c25a807b9a96ff29d8c78021 | Slate/MessagingBanner}
 */
export const MessagingBanner: React.FC<
  IProps & IMessagingBannerPropsConstraints<IProps>
> = ({
  actionProps,
  backgroundColor,
  condensed = false,
  description,
  Icon,
  label,
  labelColor,
  variant = "neutral",
  onDismiss,
  onView,
  ref,
  ...props
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const viewRef = useDoActionWhenVisible<HTMLDivElement>(onView);
  const focusTargetOutside = useFocusTargetOutside(rootRef);
  const { isMobile } = useViewport();

  const ResolvedIcon = getMessagingBannerIcon(variant, Icon);

  const handleDismiss = useCallback(() => {
    focusTargetOutside();
    onDismiss?.();
  }, [focusTargetOutside, onDismiss]);

  let isMobileAction = false;
  let action: React.ReactNode = null;
  let trailing: React.ReactNode = null;
  if (actionProps) {
    const { mobileVariant = "chevron", ...baseActionProps } = actionProps;
    isMobileAction = isMobile && mobileVariant === "chevron";
    if (isMobileAction) {
      trailing = <MessagingBannerTapTarget actionProps={baseActionProps} />;
    } else {
      const { label: actionLabel, ...actionButtonProps } = baseActionProps;
      action = (
        <Button variant="plain" {...actionButtonProps}>
          {actionLabel}
        </Button>
      );
    }
  }
  if (!isMobileAction && onDismiss) {
    trailing = <MessagingBannerDismissButton onClick={handleDismiss} />;
  }

  const cls = messagingBannerStyle.cls({
    variant,
    condensed,
    hasTapTarget: isMobileAction,
    className: componentCls(),
  });

  const vars = messagingBannerStyle.vars({
    bgColor: backgroundColor,
    labelColor,
  });

  return (
    <div
      {...props}
      ref={mergeRefs(ref, rootRef, onView ? viewRef : undefined)}
      className={cls}
      style={vars}
      data-component-name="Slate/MessagingBanner"
      role={getMessagingBannerRole(variant)}
    >
      {ResolvedIcon ? (
        <ResolvedIcon
          width="var(--slate-dimensions-6)"
          height="var(--slate-dimensions-6)"
          color="var(--fs-messaging-banner-icon-color)"
          aria-hidden
          titleAccess=""
        />
      ) : null}
      <MessagingBannerContent
        action={action}
        description={description}
        label={label}
        variant={variant}
      />
      {trailing}
    </div>
  );
};

const messagingBannerStyle = tsv({
  base: "fs-messaging-banner",
  vars: {
    bgColor: "fs-messaging-banner-bg-color",
    labelColor: "fs-messaging-banner-label-text-color",
  },
  variants: {
    variant: {
      neutral: "fs-messaging-banner--neutral",
      info: "fs-messaging-banner--info",
      success: "fs-messaging-banner--success",
      warning: "fs-messaging-banner--warning",
      critical: "fs-messaging-banner--critical",
      custom: "",
    },
    condensed: { true: "fs-messaging-banner--condensed" },
    hasTapTarget: { true: "fs-messaging-banner--has-tap-target" },
  },
});
